import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { accessToken, keepTokens } from "./auth/session";

// Biometric lock (STORY-078): when the student turned it on, the native app asks before
// showing anything; the web never has the capability, so nothing happens there.
import { capabilities } from "./platform/capabilities";
import { nsKey } from "./platform/storage";
if (capabilities.biometric && localStorage.getItem(nsKey("bio_lock")) === "1") {
  void capabilities.biometric.authenticate("unlock").then((ok) => {
    if (!ok) document.body.innerHTML = "";
  });
}

// The desktop shell opens the app with the local host's token (STORY-073); keep it and
// drop it from the address bar before anything renders.
{
  const url = new URL(window.location.href);
  const hostToken = url.searchParams.get("host_token");
  if (hostToken) {
    keepTokens(hostToken, null, true);
    url.searchParams.delete("host_token");
    window.history.replaceState(null, "", url.toString());
  }
  // Storage was cleared (a sign-out, a reset): ask the shell for the token again.
  if (capabilities.hostToken && !accessToken()) {
    void capabilities.hostToken().then((tok) => {
      if (tok) keepTokens(tok, null, true);
    });
  }
}
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "./i18n";
import "./styles.css";
import { ApiError } from "./api/client";
import { SessionProvider } from "./auth/SessionContext";
import { product } from "./product";
import { router } from "./router";
import { ConfirmProvider } from "./ui";

document.title = product.name;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 15_000,
      // Retry a connection that never landed, never an answer the server decided on.
      retry: (count, error) => !(error instanceof ApiError) && count < 1,
    },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("#root is missing from index.html");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        {/* Confirmations are rendered by the app, never by the webview: the desktop shell has
            no `window.confirm` (see src/ui/Confirm.tsx). */}
        <ConfirmProvider>
          <RouterProvider router={router} />
        </ConfirmProvider>
      </SessionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
